// frontend/src/utils/institutionLocalStorage.ts
/**
 * Institution-scoped localStorage helper factory.
 *
 * Workstream M item 2 (2026-05-19) — extracted from the parallel cache
 * scaffolding in `ActiveInstitutionContext` (keyed on `supabaseUserId`)
 * and `InstitutionConfigContext` (keyed on `slug`). Both contexts repeated
 * the same try/catch + JSON.parse/stringify + scoped-key pattern; this
 * helper centralizes it so future per-institution localStorage needs
 * don't reinvent the wheel.
 *
 * Why a factory instead of a React hook:
 * - Both call sites invoke the cache outside React's render context
 *   (module-load for legacy-key cleanup, `useEffect` for runtime
 *   load/save). A factory returning `{load, save, clear}` matches that
 *   shape directly without forcing the caller into a hook.
 * - The helper has zero React dependencies; it's pure DOM API + JSON.
 *
 * Usage:
 *
 *   const cache = makeInstitutionLocalStorage<MyType>('my_app_my_key', {
 *     ttlMs: 60 * 60 * 1000,                  // optional TTL
 *     legacyKeysToRemove: ['my_app_old_key'], // one-time cleanup on construction
 *   });
 *   const value = cache.load(scope);    // scope = supabaseUserId, slug, etc.
 *   cache.save(scope, newValue);
 *   cache.clear(scope);
 */

interface CacheOptions {
  /** Optional TTL in ms. If set, `save` wraps the value with a timestamp;
   *  `load` returns null when the wrapped value is older than `ttlMs`. */
  ttlMs?: number;
  /** Legacy keys to remove on factory construction (one-time migration
   *  for cache-key version bumps). Idempotent — re-imports are no-ops. */
  legacyKeysToRemove?: readonly string[];
}

interface TimestampedCache<T> {
  _value: T;
  _cachedAt: number;
}

export interface InstitutionLocalStorage<T> {
  /** Read the cached value for `scope`. Returns null on cache miss,
   *  TTL-expired entry, parse error, or localStorage unavailable. */
  load(scope: string): T | null;
  /** Write `value` to the cache for `scope`. Best-effort — quota errors
   *  and localStorage-unavailable are swallowed silently. */
  save(scope: string, value: T): void;
  /** Remove the cached entry for `scope`. Best-effort. */
  clear(scope: string): void;
}

const NS_SEPARATOR = ':';

const isLocalStorageAvailable = (): boolean => {
  try {
    return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
  } catch {
    return false;
  }
};

export function makeInstitutionLocalStorage<T>(
  keyPrefix: string,
  options: CacheOptions = {},
): InstitutionLocalStorage<T> {
  const { ttlMs, legacyKeysToRemove } = options;

  // One-time legacy-key cleanup. Fires when the helper is constructed
  // (typically at module import time of a context file). Subsequent
  // constructions remove already-removed keys → no-op.
  if (legacyKeysToRemove && isLocalStorageAvailable()) {
    for (const legacyKey of legacyKeysToRemove) {
      try {
        window.localStorage.removeItem(legacyKey);
      } catch {
        // ignored — localStorage may be unavailable (private mode, ssr)
      }
    }
  }

  const scopedKey = (scope: string): string => `${keyPrefix}${NS_SEPARATOR}${scope}`;

  return {
    load(scope: string): T | null {
      if (!isLocalStorageAvailable()) return null;
      try {
        const raw = window.localStorage.getItem(scopedKey(scope));
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        // TTL-wrapped path: unwrap, check timestamp, return inner value.
        if (ttlMs !== undefined && parsed && typeof parsed === 'object' && '_cachedAt' in parsed) {
          const wrapped = parsed as TimestampedCache<T>;
          const age = Date.now() - (wrapped._cachedAt || 0);
          if (age > ttlMs) return null;
          return wrapped._value;
        }
        // Non-TTL path: return parsed value directly.
        return parsed as T;
      } catch {
        return null;
      }
    },

    save(scope: string, value: T): void {
      if (!isLocalStorageAvailable()) return;
      try {
        const payload: string =
          ttlMs !== undefined
            ? JSON.stringify({ _value: value, _cachedAt: Date.now() } as TimestampedCache<T>)
            : JSON.stringify(value);
        window.localStorage.setItem(scopedKey(scope), payload);
      } catch {
        // best-effort — quota errors etc. shouldn't break the app
      }
    },

    clear(scope: string): void {
      if (!isLocalStorageAvailable()) return;
      try {
        window.localStorage.removeItem(scopedKey(scope));
      } catch {
        // best-effort
      }
    },
  };
}
